/**
 * 全局自定义指令（同构：SSR 空钩子，客户端完整实现）
 *
 * v-ellipsis
 *   文本超出时截断显示省略号，鼠标悬停弹出浮层显示完整内容。
 *   传入 false 可禁用：v-ellipsis="false"
 */

// ─── 客户端实现 ────────────────────────────────────────────────────────────────

/** 每个绑定元素对应独立的 tooltip，WeakMap 保证 GC 自动释放 */
const tooltipMap = new WeakMap<HTMLElement, HTMLDivElement>();

function getOrCreateTooltip(el: HTMLElement): HTMLDivElement {
	if (!tooltipMap.has(el)) {
		const tip = document.createElement('div');
		tip.style.display = 'none';
		document.body.appendChild(tip);
		tooltipMap.set(el, tip);
	}
	return tooltipMap.get(el)!;
}

function destroyTooltip(el: HTMLElement) {
	const tip = tooltipMap.get(el);
	if (tip) {
		tip.remove();
		tooltipMap.delete(el);
	}
}

function handleMouseEnter(this: HTMLElement, e: MouseEvent) {
	const tip = getOrCreateTooltip(this);
	const maxWidth = 600;

	let cssText = `
		max-width: ${maxWidth}px;
		overflow: auto;
		position: fixed;
		background: #262627;
		color: #fff;
		border-radius: 4px;
		line-height: 20px;
		padding: 8px 10px;
		display: block;
		font-size: 12px;
		z-index: 99999;
		word-break: break-all;
		box-shadow: 0 2px 12px rgba(0,0,0,.3);
		pointer-events: none;
	`;

	cssText +=
		window.innerWidth - e.clientX < maxWidth
			? `right:${window.innerWidth - e.clientX + 10}px;`
			: `left:${e.clientX + 16}px;`;

	cssText +=
		window.innerHeight - e.clientY < 200
			? `bottom:${window.innerHeight - e.clientY + 6}px;`
			: `top:${e.clientY + 4}px;`;

	tip.style.cssText = cssText;
	tip.textContent = this.innerText;
}

function handleMouseLeave(this: HTMLElement) {
	const tip = tooltipMap.get(this);
	if (tip) tip.style.display = 'none';
}

function bindEllipsis(el: HTMLElement, enabled: boolean) {
	el.removeEventListener('mouseenter', handleMouseEnter as EventListener);
	el.removeEventListener('mouseleave', handleMouseLeave as EventListener);

	if (!enabled) {
		el.style.overflow = '';
		el.style.textOverflow = '';
		el.style.whiteSpace = '';
		destroyTooltip(el);
		return;
	}

	el.style.overflow = 'hidden';
	el.style.textOverflow = 'ellipsis';
	el.style.whiteSpace = 'nowrap';

	if (el.scrollWidth > el.offsetWidth) {
		el.addEventListener('mouseenter', handleMouseEnter as EventListener);
		el.addEventListener('mouseleave', handleMouseLeave as EventListener);
	}
}

// ─── Nuxt 插件注册 ─────────────────────────────────────────────────────────────

export default defineNuxtPlugin(nuxtApp => {
	nuxtApp.vueApp.directive<HTMLElement, boolean | undefined>('ellipsis', {
		// SSR 阶段：getSSRProps 返回内联样式，使服务端输出已含截断 CSS
		getSSRProps(binding) {
			if (binding.value === false) return {};
			return {
				style: {
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				},
			};
		},

		// 客户端挂载
		beforeMount(el, binding) {
			if (!import.meta.client) return;
			bindEllipsis(el, binding.value !== false);
		},

		// 数据更新后重新绑定
		updated(el, binding) {
			if (!import.meta.client) return;
			bindEllipsis(el, binding.value !== false);
		},

		// 卸载时清理 DOM & 事件
		unmounted(el) {
			if (!import.meta.client) return;
			el.removeEventListener('mouseenter', handleMouseEnter as EventListener);
			el.removeEventListener('mouseleave', handleMouseLeave as EventListener);
			destroyTooltip(el);
		},
	});
});
